/** Карточка акции в ленте — порт cardHTML() из прототипа. */
import type { FeedCard } from '@allswipes/client';
import { Badge, BrandMark, Countdown, Icon, VerifiedMark, fmtShortDay, num, ticketChance } from '@allswipes/ui';
import { Link } from 'react-router-dom';

import { usePlural, useT } from '@allswipes/i18n';

export function CampaignCard({ card }: { card: FeedCard }) {
  const plural = usePlural();
  const t = useT();
  const { campaign: c, brand, instant } = card;
  const isDraw = Boolean(c.draw_at);

  return (
    <Link to={`/c/${c.id}`} className="ccard reveal">
      <div className="top">
        <BrandMark name={brand?.name ?? c.prefix} logoUrl={brand?.logo_url} size={30} />
        <span className="bname">
          {brand?.name ?? '—'}
          {brand?.verified_at && (
            <VerifiedMark
              className="bmark"
              size={13}
              title={t('Бренд проверен платформой: мы подтвердили, кто стоит за акциями')}
            />
          )}
        </span>
        {/*
          «Сразу» — свойство механики, а не галочка партнёра. Означает: человек
          получает обещанное в момент участия, и забирать потом нечего — ни
          розыгрыша, ни очереди, ни точки выдачи. У колеса исход тоже
          мгновенный, но приз по нему забирают позже, поэтому пометки там нет.
        */}
        {instant && (
          <Badge tone="accent">{t('мгновенно')}</Badge>
        )}
        <StatusBadge card={card} />
      </div>

      <div className="prize">
        {c.prize_title || c.name}
        {c.prizes_verified_at && (
          <VerifiedMark
            className="vmark"
            size={14}
            title={t('Призы проверены платформой: партнёр приложил доказательство, мы его посмотрели')}
          />
        )}
      </div>
      {c.tagline && (
        <div className="sub" style={{ marginTop: 5 }}>
          {c.tagline}
        </div>
      )}
      {/* Скрытая карточка: в ленте остаётся название и бренд, а приз и
          условия открывает ссылка. Говорим об этом прямо — замок без
          объяснения выглядит как поломка. */}
      {c.locked && (
        <div className="sub lockline" style={{ marginTop: 5 }}>
          <Icon name="shield" size={12} />
          {t('условия открываются по ссылке бренда')}
        </div>
      )}

      <div className="foot">
        <span className="mt">
          <Icon name={c.status === 'drawn' ? 'trophy' : 'clock'} size={12} />
          {c.status === 'drawn' ? (
            t('итоги')
          ) : c.status === 'scheduled' ? (
            t('старт {day}', { day: fmtShortDay(c.start_at) })
          ) : isDraw && c.draw_at ? (
            <Countdown to={c.draw_at} />
          ) : (
            t('до {day}', { day: fmtShortDay(c.end_at) })
          )}
        </span>
        <span className="spacer" />
        {c.participants > 0 && (
          <span className="mt mono" title={`${num(c.participants)} ${plural(c.participants, 'участник', 'участника', 'участников')}`}>
            <Icon name="user" size={12} />
            {num(c.participants)}
          </span>
        )}
      </div>

      {isDraw && ticketChance(c.winner_count, c.tickets) !== '' && (
        <div className="faint" style={{ fontSize: 11.5, marginTop: 6 }}>
          {t('шанс одного билета — {chance}%', { chance: ticketChance(c.winner_count, c.tickets) })}
        </div>
      )}
    </Link>
  );
}

function StatusBadge({ card }: { card: FeedCard }) {
  const t = useT();
  const plural = usePlural();
  const { campaign: c, my_tickets: mine } = card;
  if (mine > 0) {
    return (
      <Badge tone="prize">
        {mine} {plural(mine, t('билет'), t('билета'), t('билетов'))}
      </Badge>
    );
  }
  switch (c.status) {
    case 'active':
      return (
        <Badge tone="live" dot>
          {t('идёт')}
        </Badge>
      );
    case 'scheduled':
      return <Badge>{t('скоро')}</Badge>;
    case 'ended':
      return <Badge tone="warn">{t('ждём розыгрыш')}</Badge>;
    case 'drawn':
      return <Badge tone="good">{t('итоги')}</Badge>;
    default:
      return null;
  }
}
